import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AssinaturaEditPlanModal } from "./AssinaturaEditPlanModal";
import { AssinaturaCancelModal } from "./AssinaturaCancelModal";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ProfissionalAssinaturaProps {
  professionalId: string;
  professionalName: string;
}

interface Subscription {
  id: string;
  status: string;
  plan_code: string;
  price_cents: number;
  renewal_day: number;
  started_at: string;
  next_billing_date: string | null;
  payment_method: string | null;
}

export function ProfissionalAssinatura({ professionalId, professionalName }: ProfissionalAssinaturaProps) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, [professionalId]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", professionalId)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setSubscription(data);
    } catch (error) {
      console.error("Error loading subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      active: "default",
      canceled: "secondary",
      pending: "secondary",
    };
    const labels: Record<string, string> = {
      active: "Ativa",
      canceled: "Cancelada",
      pending: "Pendente",
    };
    return (
      <Badge variant={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return <p>Carregando...</p>;
  }

  if (!subscription) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">Nenhuma assinatura ativa</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Assinatura Atual</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Plano</p>
              <p className="font-medium">{subscription.plan_code}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              {getStatusBadge(subscription.status)}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor Mensal</p>
              <p className="font-medium">
                R$ {(subscription.price_cents / 100).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Dia de Renovação</p>
              <p className="font-medium">Dia {subscription.renewal_day}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Iniciada em</p>
              <p className="font-medium">
                {format(new Date(subscription.started_at), "dd/MM/yyyy", { locale: ptBR })}
              </p>
            </div>
            {subscription.next_billing_date && (
              <div>
                <p className="text-sm text-muted-foreground">Próximo Pagamento</p>
                <p className="font-medium">
                  {format(new Date(subscription.next_billing_date), "dd/MM/yyyy", { locale: ptBR })}
                </p>
              </div>
            )}
            {subscription.payment_method && (
              <div>
                <p className="text-sm text-muted-foreground">Forma de Pagamento</p>
                <p className="font-medium">{subscription.payment_method}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={() => setShowEditModal(true)}>
              Trocar Plano
            </Button>
            {subscription.status === "active" && (
              <Button
                variant="destructive"
                onClick={() => setShowCancelModal(true)}
              >
                Cancelar Assinatura
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {showEditModal && (
        <AssinaturaEditPlanModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          subscriptionId={subscription.id}
          currentPlanCode={subscription.plan_code}
          onSuccess={loadSubscription}
        />
      )}

      {showCancelModal && (
        <AssinaturaCancelModal
          open={showCancelModal}
          onOpenChange={setShowCancelModal}
          subscriptionId={subscription.id}
          professionalName={professionalName}
          onSuccess={loadSubscription}
        />
      )}
    </div>
  );
}